import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { CheckCircle2, AlertCircle, Clock } from "lucide-react";

/** Pagina PUBLIC di verifica email — funziona anche senza sessione (scheda anonima). */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | expired | invalid | missing
  const [message, setMessage] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus("missing");
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;
    (async () => {
      try {
        const res = await api.post("/auth/verify-email", { token });
        setMessage(res.data.message || "Email verificata con successo");
        setStatus("ok");
      } catch (e) {
        const code = e?.response?.status;
        const detail = e?.response?.data?.detail || "Errore imprevisto";
        setMessage(detail);
        setStatus(code === 410 ? "expired" : "invalid");
      }
    })();
  }, [token]);

  const cfg = {
    loading: { Icon: Clock, tint: "text-muted-foreground", title: "Verifica in corso…", body: "Un attimo, sto controllando il tuo link." },
    ok: { Icon: CheckCircle2, tint: "text-primary", title: "Email verificata ✓", body: message || "Il tuo indirizzo è confermato. Puoi tornare all'app quando vuoi." },
    expired: { Icon: AlertCircle, tint: "text-secondary", title: "Link scaduto", body: message || "Il link è scaduto (48h). Accedi all'app per richiedere un nuovo link." },
    invalid: { Icon: AlertCircle, tint: "text-secondary", title: "Link non valido", body: message || "Il link non è più valido o è già stato usato. Se hai già verificato, prova ad accedere." },
    missing: { Icon: AlertCircle, tint: "text-secondary", title: "Token mancante", body: "Il link non contiene un token. Apri il link direttamente dall'email che ti abbiamo mandato." },
  }[status];
  const { Icon, tint, title, body } = cfg;

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center mb-8 justify-center" data-testid="verify-brand-link">
          <Logo />
        </Link>
        <Card className="p-8 rounded-2xl border">
          <div className="flex flex-col items-center text-center">
            <div className={`w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4 ${tint}`}>
              <Icon className="w-7 h-7" />
            </div>
            <h1 className="font-heading font-bold text-2xl mb-2" data-testid={`verify-title-${status}`}>{title}</h1>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{body}</p>
            <div className="w-full space-y-2">
              <Button asChild className="w-full h-11 rounded-full" data-testid="verify-go-to-app">
                <Link to="/app">Vai all'app</Link>
              </Button>
              {status !== "ok" && (
                <Button asChild variant="outline" className="w-full h-11 rounded-full" data-testid="verify-go-to-login">
                  <Link to="/login">Accedi per richiedere un nuovo link</Link>
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
